import { forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from 'react';
import { cn } from '../../../utils';

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /** Use modern styling with rounded corners and subtle dividers */
  variant?: 'default' | 'modern';
}

const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <div
      className={cn(
        'relative w-full overflow-auto',
        variant === 'modern' && 'bg-surface border border-border rounded-xl overflow-hidden'
      )}
    >
      <table
        ref={ref}
        className={cn('w-full caption-bottom text-sm', className)}
        {...props}
      />
    </div>
  )
);
Table.displayName = 'Table';

export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  variant?: 'default' | 'modern';
}

const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <thead
      ref={ref}
      className={cn(
        variant === 'modern'
          ? 'bg-[var(--table-band)] border-b border-border'
          : '[&_tr]:border-b',
        className
      )}
      {...props}
    />
  )
);
TableHeader.displayName = 'TableHeader';

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  variant?: 'default' | 'modern';
}

const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <tbody
      ref={ref}
      className={cn(
        variant === 'modern'
          ? 'divide-y divide-border'
          : '[&_tr:last-child]:border-0',
        className
      )}
      {...props}
    />
  )
);
TableBody.displayName = 'TableBody';

export interface TableFooterProps extends HTMLAttributes<HTMLTableSectionElement> {}

const TableFooter = forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, ...props }, ref) => (
    <tfoot
      ref={ref}
      className={cn(
        'border-t bg-[var(--table-band)] font-medium [&>tr]:last:border-b-0',
        className
      )}
      {...props}
    />
  )
);
TableFooter.displayName = 'TableFooter';

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  variant?: 'default' | 'modern';
}

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'transition-colors data-[state=selected]:bg-[var(--surface-selected)]',
        variant === 'modern'
          ? 'hover:bg-surface-hover'
          : 'border-b hover:bg-surface-hover',
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  variant?: 'default' | 'modern';
}

const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        'h-12 px-4 text-left align-middle font-medium [&:has([role=checkbox])]:pr-0',
        variant === 'modern'
          ? 'text-xs text-muted-foreground uppercase tracking-wider'
          : 'text-text-muted',
        className
      )}
      {...props}
    />
  )
);
TableHead.displayName = 'TableHead';

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {}

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        'p-4 align-middle text-text-primary [&:has([role=checkbox])]:pr-0',
        className
      )}
      {...props}
    />
  )
);
TableCell.displayName = 'TableCell';

export interface TableCaptionProps extends HTMLAttributes<HTMLTableCaptionElement> {}

const TableCaption = forwardRef<HTMLTableCaptionElement, TableCaptionProps>(
  ({ className, ...props }, ref) => (
    <caption
      ref={ref}
      className={cn('mt-4 text-sm text-text-muted', className)}
      {...props}
    />
  )
);
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
